import { fetchMessagesForPatientsInRange, buildMessageReport } from './messageReportService';
import { fetchCareAlertsReportData, buildCareAlertsReport } from './careAlertsReportService';
import { buildResponseTimeReport } from './responseTimeReportService';
import { buildDiseaseDemographicsReport } from './diseaseReportService';
import {
  sumMonthlyRollups,
  fetchRawEncounters,
  buildCareTimeSummary,
  buildCareTimeByPatient,
} from './careTimeReportService';
import { fetchVolumeActivityInputs, buildVolumeActivityReport } from './volumeActivityReportService';
import { resolvePatientListingAsOf, buildPatientListingReport } from './patientListingReportService';
import { filterRpmPeriodCohort } from './rpmPeriodCohort';
import { buildProSummaryForContext } from './proReportService';
import { ymdLocal } from './rpmCensusUtils';
import { priorBlockYmKeys } from '../utils/reportFormat';

const EMPTY_RPM_INPUTS = { programsByPatientId: new Map(), holdAudits: [] };

/**
 * Per-run context for building client-wide reports. Fetches shared by several
 * reports (RPM programs + hold audits) are cached so a package or freeze run
 * reads them once per patient group.
 */
export function createReportBuildContext({ tenantId, patients, clients, clientSites, period, userNameById }) {
  let rpmInputsPromise = null;
  const pids = (patients || []).map((p) => p.id);
  return {
    tenantId,
    patients: patients || [],
    pids,
    clients: clients || [],
    clientSites: clientSites || [],
    period,
    userNameById: userNameById || new Map(),
    getRpmInputs() {
      if (!pids.length) return Promise.resolve(EMPTY_RPM_INPUTS);
      if (!rpmInputsPromise) rpmInputsPromise = fetchVolumeActivityInputs(tenantId, pids);
      return rpmInputsPromise;
    },
    async getRpmPeriodCohort() {
      const inputs = await this.getRpmInputs();
      return filterRpmPeriodCohort({ patients: this.patients, ...inputs, period });
    },
  };
}

/**
 * Build one client-wide report from a context. Returns `{ type, report,
 * periodLabelOverride? }` or null for unknown ids. Every report that the Client
 * report package, report snapshots, and the explainer describe is built here.
 */
export async function buildClientReport(reportId, ctx) {
  const { tenantId, patients, pids, clients, clientSites, period, userNameById } = ctx;
  const start = period?.start ?? null;
  const end = period?.end ?? null;
  const ymKeys = period?.ymKeys ?? [];

  if (reportId === 'messages') {
    if (!pids.length) return { type: reportId, report: buildMessageReport([], null, { start, end }) };
    const { messages, conversationIdSet } = await fetchMessagesForPatientsInRange(patients, start, end);
    return { type: reportId, report: buildMessageReport(messages, conversationIdSet, { start, end }) };
  }

  if (reportId === 'pro-summary') {
    const report = await buildProSummaryForContext(ctx);
    return report ? { type: reportId, report } : null;
  }

  if (reportId === 'care-alerts') {
    const cohort = await ctx.getRpmPeriodCohort();
    const ids = cohort.map((p) => p.id);
    const data = ids.length
      ? await fetchCareAlertsReportData(tenantId, ids, start, end)
      : { ackRows: [], createdCohort: [] };
    const report = buildCareAlertsReport(data, { excludeHistorical: true, userNameById, periodEnd: end });
    return { type: reportId, report: { ...report, cohortPatientCount: ids.length } };
  }

  if (reportId === 'care-time') {
    const [rollups, raw] = pids.length
      ? await Promise.all([
          sumMonthlyRollups(pids, ymKeys),
          fetchRawEncounters(tenantId, pids, ymdLocal(start), ymdLocal(end)),
        ])
      : [new Map(), { rows: [], truncated: false }];
    const report = buildCareTimeSummary({
      rollupByPatient: rollups,
      rawEncounters: raw.rows,
      rawTruncated: raw.truncated,
    });
    return { type: reportId, report };
  }

  if (reportId === 'response-time-messages') {
    if (!pids.length) return { type: reportId, report: buildResponseTimeReport({ messages: [], range: { start, end } }) };
    const { messages, conversationIdSet } = await fetchMessagesForPatientsInRange(patients, start, end);
    return {
      type: reportId,
      report: buildResponseTimeReport({ messages, inScopeIds: conversationIdSet, range: { start, end } }),
    };
  }

  if (reportId === 'response-time-alerts') {
    const alertData = pids.length
      ? await fetchCareAlertsReportData(tenantId, pids, start, end)
      : { ackRows: [], createdCohort: [] };
    const report = buildResponseTimeReport({
      alertCohort: alertData.createdCohort,
      range: { start, end },
      excludeHistorical: true,
    });
    return { type: reportId, report };
  }

  if (reportId === 'disease') {
    const cohort = await ctx.getRpmPeriodCohort();
    return { type: reportId, report: buildDiseaseDemographicsReport(cohort, { period }) };
  }

  if (reportId === 'care-time-by-patient') {
    const priorYmKeys = priorBlockYmKeys(ymKeys);
    const [cur, prev] = pids.length
      ? await Promise.all([sumMonthlyRollups(pids, ymKeys), sumMonthlyRollups(pids, priorYmKeys)])
      : [new Map(), new Map()];
    const patientById = new Map(patients.map((p) => [p.id, p]));
    return {
      type: reportId,
      report: buildCareTimeByPatient({ currentByPatient: cur, priorByPatient: prev, patientById }),
    };
  }

  if (reportId === 'volume-activity' || reportId === 'volume-activity-summary') {
    const inputs = await ctx.getRpmInputs();
    const report = buildVolumeActivityReport({
      patients,
      programsByPatientId: inputs.programsByPatientId,
      holdAudits: inputs.holdAudits,
      clients,
      clientSites,
      period,
    });
    return { type: reportId, report };
  }

  if (reportId === 'patient-listing') {
    const inputs = await ctx.getRpmInputs();
    const asOf = resolvePatientListingAsOf('custom', ymdLocal(end));
    const report = buildPatientListingReport({
      patients,
      programsByPatientId: inputs.programsByPatientId,
      holdAudits: inputs.holdAudits,
      clients,
      clientSites,
      userNameById,
      asOf,
    });
    return { type: reportId, report, periodLabelOverride: asOf?.label || '' };
  }

  return null;
}
